import { BACKGROUND_IMAGES } from 'modules/map/components/daily-dish/config';
import type { HolidayWindow, LookPalette, LookWeekday, WeeklyLookConfig } from './types';

const bgFile = (id: number): string => BACKGROUND_IMAGES[1][id];

const IMAGES_2010 = Object.values(BACKGROUND_IMAGES[2]);
const IMAGES_CHRISTMAS = [bgFile(10), bgFile(11)];
const IMAGES_NOEL = [bgFile(12)];
const IMAGES_GIAI_PHONG = [bgFile(13)];
const IMAGES_TET = [bgFile(16), bgFile(17), bgFile(18)];

const THEMED_IMAGES = new Set([
  ...IMAGES_2010,
  ...IMAGES_CHRISTMAS,
  ...IMAGES_NOEL,
  ...IMAGES_GIAI_PHONG,
  ...IMAGES_TET,
]);

const GENERAL_IMAGES = Object.values(BACKGROUND_IMAGES[1]).filter(
  (file) => !THEMED_IMAGES.has(file)
);

const weekdayColors = (
  colors: [string, string, string, string, string, string]
): LookPalette['weekday'] =>
  ({
    2: { borderColor: colors[0] },
    3: { borderColor: colors[1] },
    4: { borderColor: colors[2] },
    5: { borderColor: colors[3] },
    6: { borderColor: colors[4] },
    7: { borderColor: colors[5] },
  }) as Record<LookWeekday, { borderColor: string }>;

const palette = (
  partial: Omit<LookPalette, 'weekday'> & {
    colors: [string, string, string, string, string, string];
  }
): LookPalette => {
  const { colors, ...rest } = partial;
  return { ...rest, weekday: weekdayColors(colors) };
};

const GENERAL_PALETTES: LookPalette[] = [
  palette({
    borderWidth: 10,
    borderRadius: 12,
    fontFamilyId: 1,
    weekTitleColor: '#283090',
    subWeekTitleColor: '#283090',
    weekdayTitleColor: '#ffffff',
    contentColor: '#000000',
    colors: ['#FFAE00', '#008DDA', '#00B898', '#9747FF', '#46B52B', '#FFEE00'],
  }),
  palette({
    borderWidth: 8,
    borderRadius: 14,
    fontFamilyId: 2,
    weekTitleColor: '#DC2626',
    subWeekTitleColor: '#DC2626',
    weekdayTitleColor: '#ffffff',
    contentColor: '#000000',
    colors: ['#FF5A1F', '#00C2D1', '#E11D48', '#7C3AED', '#22C55E', '#FACC15'],
  }),
  palette({
    borderWidth: 12,
    borderRadius: 10,
    fontFamilyId: 3,
    weekTitleColor: '#EA580C',
    subWeekTitleColor: '#EA580C',
    weekdayTitleColor: '#ffffff',
    contentColor: '#000000',
    colors: ['#F97316', '#06B6D4', '#EC4899', '#8B5CF6', '#84CC16', '#FBBF24'],
  }),
  palette({
    borderWidth: 10,
    borderRadius: 16,
    fontFamilyId: 11,
    weekTitleColor: '#2563EB',
    subWeekTitleColor: '#2563EB',
    weekdayTitleColor: '#ffffff',
    contentColor: '#000000',
    colors: ['#EF4444', '#3B82F6', '#F59E0B', '#10B981', '#A855F7', '#F43F5E'],
  }),
];

const PALETTES_2010: LookPalette[] = [
  palette({
    borderWidth: 10,
    borderRadius: 16,
    fontFamilyId: 7,
    weekTitleColor: '#E11D48',
    subWeekTitleColor: '#E11D48',
    weekdayTitleColor: '#ffffff',
    contentColor: '#000000',
    colors: ['#F43F5E', '#F59E0B', '#EC4899', '#E11D48', '#FB7185', '#A855F7'],
  }),
  palette({
    borderWidth: 8,
    borderRadius: 18,
    fontFamilyId: 8,
    weekTitleColor: '#DB2777',
    subWeekTitleColor: '#DB2777',
    weekdayTitleColor: '#ffffff',
    contentColor: '#000000',
    colors: ['#FB7185', '#FBBF24', '#F472B6', '#E11D48', '#C026D3', '#F97316'],
  }),
];

const PALETTES_TET: LookPalette[] = [
  palette({
    borderWidth: 12,
    borderRadius: 10,
    fontFamilyId: 18,
    weekTitleColor: '#DC2626',
    subWeekTitleColor: '#DC2626',
    weekdayTitleColor: '#ffffff',
    contentColor: '#000000',
    colors: ['#DC2626', '#EAB308', '#F97316', '#CA8A04', '#EF4444', '#84CC16'],
  }),
  palette({
    borderWidth: 10,
    borderRadius: 12,
    fontFamilyId: 19,
    weekTitleColor: '#B91C1C',
    subWeekTitleColor: '#B91C1C',
    weekdayTitleColor: '#ffffff',
    contentColor: '#000000',
    colors: ['#EF4444', '#F59E0B', '#F97316', '#EAB308', '#DC2626', '#22C55E'],
  }),
];

const solar = (
  id: string,
  start: string,
  end: string,
  images: string[] = [],
  palettes: LookPalette[] = []
): HolidayWindow => ({
  id,
  everyYear: true,
  start,
  end,
  images,
  palettes,
});

const dated = (
  id: string,
  start: string,
  end: string,
  images: string[] = [],
  palettes: LookPalette[] = []
): HolidayWindow => ({
  id,
  everyYear: false,
  start,
  end,
  images,
  palettes,
});

export const WEEKLY_LOOK_CONFIG: WeeklyLookConfig = {
  general: {
    images: GENERAL_IMAGES,
    palettes: GENERAL_PALETTES,
  },
  windows: [
    solar('khai-giang', '09-05', '09-05'),
    solar('2010', '10-20', '10-20', IMAGES_2010, PALETTES_2010),
    solar('nha-giao', '11-20', '11-20'),
    solar('tet-duong', '01-01', '01-01'),
    solar('hs-sv', '01-09', '01-09'),
    solar('quoc-te-phu-nu', '03-08', '03-08'),
    solar('doan', '03-26', '03-26'),
    solar('earth-day', '04-22', '04-22'),
    solar('giai-phong-lao-dong', '04-30', '05-01', IMAGES_GIAI_PHONG),
    solar('thieu-nhi', '06-01', '06-01'),
    solar('halloween', '10-31', '10-31'),
    solar('giang-sinh', '12-24', '12-25', IMAGES_CHRISTMAS),
    solar('noel', '12-24', '12-25', IMAGES_NOEL),
    dated('tet-2026', '2026-02-14', '2026-02-23', IMAGES_TET, PALETTES_TET),
    dated('tet-2027', '2027-02-03', '2027-02-12', IMAGES_TET, PALETTES_TET),
    dated('tet-2028', '2028-01-23', '2028-02-01', IMAGES_TET, PALETTES_TET),
    dated('trung-thu-2026', '2026-09-25', '2026-09-25'),
    dated('trung-thu-2027', '2027-09-15', '2027-09-15'),
    dated('trung-thu-2028', '2028-10-03', '2028-10-03'),
    dated('gio-to-2026', '2026-04-26', '2026-04-26'),
    dated('gio-to-2027', '2027-04-16', '2027-04-16'),
    dated('gio-to-2028', '2028-04-04', '2028-04-04'),
  ],
};

export const WINDOW_LABELS: Record<string, string> = {
  general: 'Ảnh chung',
  'khai-giang': 'Khai giảng',
  '2010': '20/10 Phụ nữ VN',
  'nha-giao': '20/11 Nhà giáo VN',
  'tet-duong': 'Tết Dương lịch',
  'hs-sv': 'Học sinh – Sinh viên',
  'quoc-te-phu-nu': '8/3 Quốc tế Phụ nữ',
  doan: '26/3 Thành lập Đoàn',
  'earth-day': 'Ngày Trái đất',
  'giai-phong-lao-dong': '30/4 – 1/5',
  'thieu-nhi': '1/6 Quốc tế Thiếu nhi',
  halloween: 'Halloween',
  'giang-sinh': 'Christmas',
  noel: 'Noel',
  'tet-2026': 'Tết 2026',
  'tet-2027': 'Tết 2027',
  'tet-2028': 'Tết 2028',
  'trung-thu-2026': 'Trung thu 2026',
  'trung-thu-2027': 'Trung thu 2027',
  'trung-thu-2028': 'Trung thu 2028',
  'gio-to-2026': 'Giỗ Tổ 2026',
  'gio-to-2027': 'Giỗ Tổ 2027',
  'gio-to-2028': 'Giỗ Tổ 2028',
};
